/*!
 * HoursRight - a tiny, dependency-free "Open Now / Business Hours" widget.
 * Vendly LLC. Rentable micro-app. No build step, no libraries.
 *
 * ---------------------------------------------------------------------------
 * HOW IT WORKS
 *   1. Drop a container:      <div id="hoursright"></div>
 *   2. Configure + load:
 *        <script>
 *          window.HoursRightConfig = {
 *            target: "#hoursright",
 *            timezone: "America/New_York",
 *            name: "Palmetto Precision Strings",
 *            hours: {
 *              sun: [],                       // closed
 *              mon: [["09:00","17:00"]],      // 9a-5p
 *              tue: [["09:00","17:00"]],
 *              wed: [["09:00","17:00"]],
 *              thu: [["09:00","17:00"]],
 *              fri: [["09:00","17:00"]],
 *              sat: [["10:00","14:00"]]       // 10a-2p
 *              // multiple ranges allowed for lunch breaks:
 *              // mon: [["09:00","12:00"],["13:00","17:00"]]
 *              // overnight allowed (close <= open spans past midnight):
 *              // fri: [["18:00","02:00"]]
 *            },
 *            holidays: [
 *              { date: "2026-12-25", closed: true, name: "Christmas" },
 *              { date: "2026-07-04", open: "10:00", close: "14:00", name: "July 4th" }
 *            ]
 *          };
 *        </script>
 *        <script src="hoursright.js" defer></script>
 *
 *   Times are 24h "HH:MM". Day keys: sun mon tue wed thu fri sat.
 *   timezone is an IANA name so the widget is correct for visitors in any zone.
 * ---------------------------------------------------------------------------
 */
(function () {
  "use strict";

  var DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  var DAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  var DEFAULTS = {
    target: "#hoursright",
    timezone: "America/New_York",
    name: "",
    hours: {},
    holidays: [],
    labels: {
      openNow: "Open now",
      closed: "Closed",
      closesAt: "Closes",
      opensAt: "Opens",
      today: "Today",
      viewWeek: "View full week",
      hideWeek: "Hide hours",
      closedAllDay: "Closed"
    },
    accent: "#e8560a" // Vendly orange; clients can override
  };

  // ---- time helpers ---------------------------------------------------------

  // Parse "HH:MM" -> minutes since midnight. Returns null if invalid.
  function toMin(hhmm) {
    if (typeof hhmm !== "string") return null;
    var m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    var h = parseInt(m[1], 10), min = parseInt(m[2], 10);
    if (h > 23 || min > 59) return null;
    return h * 60 + min;
  }

  // Format minutes-since-midnight -> "9:00 AM". Handles values >= 1440 (wrap).
  function fmtMin(total) {
    total = ((total % 1440) + 1440) % 1440;
    var h = Math.floor(total / 60), m = total % 60;
    var ap = h < 12 ? "AM" : "PM";
    var h12 = h % 12; if (h12 === 0) h12 = 12;
    return h12 + ":" + (m < 10 ? "0" + m : m) + " " + ap;
  }

  // Current wall-clock in a timezone. Returns {y,mo,d,dow,minutes,dateStr}.
  function nowInTz(tz) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz, weekday: "short", hour12: false,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit"
      }).formatToParts(new Date());
    } catch (e) {
      // Bad timezone -> fall back to the visitor's local zone so the widget still renders.
      parts = new Intl.DateTimeFormat("en-US", {
        weekday: "short", hour12: false, year: "numeric", month: "2-digit",
        day: "2-digit", hour: "2-digit", minute: "2-digit"
      }).formatToParts(new Date());
    }
    var g = {};
    parts.forEach(function (p) { g[p.type] = p.value; });
    var dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    var hour = parseInt(g.hour, 10) % 24; // some engines give "24" at midnight
    return {
      y: parseInt(g.year, 10),
      mo: parseInt(g.month, 10),
      d: parseInt(g.day, 10),
      dow: dowMap[g.weekday],
      minutes: hour * 60 + parseInt(g.minute, 10),
      dateStr: g.year + "-" + g.month + "-" + g.day
    };
  }

  // Calendar date + weekday for an offset in days from a base Y-M-D.
  // Uses noon UTC to stay clear of DST date-flips; good enough for hours display.
  function dateForOffset(baseY, baseMo, baseD, offset) {
    var dt = new Date(Date.UTC(baseY, baseMo - 1, baseD, 12, 0, 0));
    dt.setUTCDate(dt.getUTCDate() + offset);
    var y = dt.getUTCFullYear();
    var mo = dt.getUTCMonth() + 1;
    var d = dt.getUTCDate();
    var pad = function (n) { return n < 10 ? "0" + n : "" + n; };
    return { dow: dt.getUTCDay(), dateStr: y + "-" + pad(mo) + "-" + pad(d) };
  }

  // Ranges (with holiday override) for a given weekday + date.
  function rangesFor(cfg, dow, dateStr) {
    var holiday = null;
    for (var i = 0; i < cfg.holidays.length; i++) {
      if (cfg.holidays[i] && cfg.holidays[i].date === dateStr) { holiday = cfg.holidays[i]; break; }
    }
    if (holiday) {
      if (holiday.closed) return { ranges: [], holiday: holiday };
      var o = toMin(holiday.open), c = toMin(holiday.close);
      if (o == null || c == null) return { ranges: [], holiday: holiday };
      return { ranges: [[o, c]], holiday: holiday };
    }
    var raw = cfg.hours[DAY_KEYS[dow]] || [];
    var out = [];
    for (var j = 0; j < raw.length; j++) {
      var a = toMin(raw[j][0]), b = toMin(raw[j][1]);
      if (a != null && b != null) out.push([a, b]);
    }
    return { ranges: out, holiday: null };
  }

  // Build absolute open intervals (minutes from midnight-today) over a horizon,
  // handling ranges that wrap past midnight. Anchor day = offset 0 = today.
  function buildIntervals(cfg, now) {
    var intervals = [];
    for (var off = -1; off <= 8; off++) {
      var info = dateForOffset(now.y, now.mo, now.d, off);
      var r = rangesFor(cfg, info.dow, info.dateStr).ranges;
      for (var k = 0; k < r.length; k++) {
        var o = r[k][0], c = r[k][1];
        var startAbs = off * 1440 + o;
        var endAbs = off * 1440 + (c > o ? c : c + 1440); // wrap => extend into next day
        if (endAbs > startAbs) intervals.push({ start: startAbs, end: endAbs });
      }
    }
    intervals.sort(function (a, b) { return a.start - b.start; });
    return intervals;
  }

  // Current status + next transition. nowAbs = now.minutes (offset-0 minutes).
  function computeStatus(cfg, now) {
    var intervals = buildIntervals(cfg, now);
    var nowAbs = now.minutes;
    var openInterval = null, nextOpen = null;
    for (var i = 0; i < intervals.length; i++) {
      var iv = intervals[i];
      if (iv.start <= nowAbs && nowAbs < iv.end) { openInterval = iv; break; }
      if (iv.start > nowAbs && nextOpen == null) nextOpen = iv;
    }
    if (openInterval) {
      return { open: true, changeAbs: openInterval.end };
    }
    // not open: find soonest interval starting after now
    if (!nextOpen) {
      for (var j = 0; j < intervals.length; j++) {
        if (intervals[j].start > nowAbs) { nextOpen = intervals[j]; break; }
      }
    }
    return { open: false, changeAbs: nextOpen ? nextOpen.start : null };
  }

  // Human label for a transition at an absolute minute value from today.
  function transitionLabel(cfg, now, changeAbs, opening) {
    if (changeAbs == null) return "";
    var dayOff = Math.floor(changeAbs / 1440);
    var minOfDay = ((changeAbs % 1440) + 1440) % 1440;
    // Hardening (2026-07-11): labels are client config; escape before they land in innerHTML.
    var verb = escapeHtml(opening ? cfg.labels.opensAt : cfg.labels.closesAt);
    var timeStr = fmtMin(minOfDay);
    if (dayOff === 0) return verb + " " + timeStr;
    var info = dateForOffset(now.y, now.mo, now.d, dayOff);
    var dayName = dayOff === 1 ? "tomorrow" : DAY_LABELS[info.dow];
    return verb + " " + dayName + " " + timeStr;
  }

  // ---- rendering ------------------------------------------------------------

  function rangesToText(cfg, ranges) {
    if (!ranges.length) return cfg.labels.closedAllDay;
    return ranges.map(function (r) { return fmtMin(r[0]) + " - " + fmtMin(r[1]); }).join(", ");
  }

  // Hardening (2026-07-11): accent goes into a <style> block (via textContent, so
  // no HTML breakout). Strip anything not part of a color token so a hostile
  // config value cannot inject extra CSS rules. Keeps #hex, rgb()/hsl(), names.
  function safeColor(c) {
    var v = String(c == null ? "" : c).slice(0, 40).replace(/[^#a-zA-Z0-9(),.%\s-]/g, "");
    return v || "#e8560a";
  }

  function injectCss(accent) {
    accent = safeColor(accent);
    if (document.getElementById("hoursright-css")) return;
    var css =
      ".hr-widget{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
      "border:1px solid #e5e5e5;border-radius:12px;padding:16px 18px;max-width:340px;background:#fff;color:#14140f;line-height:1.4;box-sizing:border-box}" +
      ".hr-top{display:flex;align-items:center;gap:10px}" +
      ".hr-dot{width:10px;height:10px;border-radius:50%;flex:0 0 auto}" +
      ".hr-status{font-weight:700;font-size:16px}" +
      ".hr-open .hr-dot{background:#1a9e4b}.hr-open .hr-status{color:#1a9e4b}" +
      ".hr-closed .hr-dot{background:#c23b3b}.hr-closed .hr-status{color:#c23b3b}" +
      ".hr-sub{color:#5c5c55;font-size:14px;margin-top:2px}" +
      ".hr-name{font-weight:600;font-size:13px;color:#8a8a80;margin-bottom:8px;letter-spacing:.02em}" +
      ".hr-toggle{margin-top:10px;background:none;border:none;color:" + accent + ";font-size:13px;font-weight:600;cursor:pointer;padding:0}" +
      ".hr-toggle:hover{text-decoration:underline}" +
      ".hr-week{margin-top:10px;border-top:1px solid #eee;padding-top:8px;display:none}" +
      ".hr-week.hr-show{display:block}" +
      ".hr-row{display:flex;justify-content:space-between;font-size:13px;padding:3px 0}" +
      ".hr-row.hr-is-today{font-weight:700}" +
      ".hr-row .hr-day{color:#14140f}.hr-row .hr-times{color:#5c5c55}" +
      ".hr-badge{margin-top:8px;font-size:11px;color:#a9a9a0}";
    var el = document.createElement("style");
    el.id = "hoursright-css";
    el.textContent = css;
    document.head.appendChild(el);
  }

  function render(container, cfg) {
    var now = nowInTz(cfg.timezone);
    var status = computeStatus(cfg, now);
    var todayRanges = rangesFor(cfg, now.dow, now.dateStr);

    var wrap = document.createElement("div");
    wrap.className = "hr-widget " + (status.open ? "hr-open" : "hr-closed");

    var html = "";
    if (cfg.name) html += '<div class="hr-name">' + escapeHtml(cfg.name) + "</div>";
    html += '<div class="hr-top"><span class="hr-dot"></span><span class="hr-status">' +
      escapeHtml(status.open ? cfg.labels.openNow : cfg.labels.closed) + "</span></div>";

    var sub = transitionLabel(cfg, now, status.changeAbs, !status.open);
    if (todayRanges.holiday && todayRanges.holiday.name) {
      sub = escapeHtml(todayRanges.holiday.name) + (sub ? " - " + sub : "");
    }
    if (sub) html += '<div class="hr-sub">' + sub + "</div>";

    html += '<button class="hr-toggle" type="button">' + escapeHtml(cfg.labels.viewWeek) + "</button>";
    html += '<div class="hr-week">';
    for (var d = 0; d < 7; d++) {
      // Show the week starting with today's weekday for relevance.
      var dow = (now.dow + d) % 7;
      var info = d === 0 ? { dateStr: now.dateStr } : dateForOffset(now.y, now.mo, now.d, d);
      var rr = rangesFor(cfg, dow, info.dateStr);
      html += '<div class="hr-row' + (d === 0 ? " hr-is-today" : "") + '">' +
        '<span class="hr-day">' + escapeHtml(d === 0 ? cfg.labels.today : DAY_LABELS[dow]) + "</span>" +
        '<span class="hr-times">' + escapeHtml(rangesToText(cfg, rr.ranges)) + "</span></div>";
    }
    html += "</div>";
    html += '<div class="hr-badge">Hours by HoursRight</div>';

    wrap.innerHTML = html;

    var toggle = wrap.querySelector(".hr-toggle");
    var week = wrap.querySelector(".hr-week");
    toggle.addEventListener("click", function () {
      var showing = week.classList.toggle("hr-show");
      toggle.textContent = showing ? cfg.labels.hideWeek : cfg.labels.viewWeek;
    });

    container.innerHTML = "";
    container.appendChild(wrap);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // ---- boot -----------------------------------------------------------------

  function mergeDefaults(user) {
    var cfg = {};
    for (var k in DEFAULTS) cfg[k] = DEFAULTS[k];
    for (var j in user) if (user.hasOwnProperty(j)) cfg[j] = user[j];
    cfg.labels = {};
    for (var l in DEFAULTS.labels) cfg.labels[l] = DEFAULTS.labels[l];
    if (user && user.labels) for (var m in user.labels) cfg.labels[m] = user.labels[m];
    return cfg;
  }

  function init(userCfg) {
    var cfg = mergeDefaults(userCfg || {});
    injectCss(cfg.accent);
    var container = document.querySelector(cfg.target);
    if (!container) {
      if (window.console) console.warn("[HoursRight] target not found:", cfg.target);
      return;
    }
    render(container, cfg);
    // Refresh at the top of each minute so the status stays live.
    setInterval(function () { render(container, cfg); }, 60 * 1000);
  }

  // Expose for manual use, and auto-init from window.HoursRightConfig.
  window.HoursRight = { init: init };
  function autostart() {
    if (window.HoursRightConfig) init(window.HoursRightConfig);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autostart);
  } else {
    autostart();
  }
})();
